<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Audit Results</h3>
      <div class="flex items-center gap-2">
        <span v-if="latestAudit && !running" class="text-xs text-gray-500 dark:text-gray-400">
          {{ formatDate(latestAudit.created_at) }}
        </span>
        <button @click="handleRunAudit" :disabled="running"
          class="btn btn-primary btn-sm">
          {{ running ? 'Auditing...' : (latestAudit ? 'Re-audit' : 'Run Audit') }}
        </button>
      </div>
    </div>

    <!-- Running state with streaming results -->
    <div v-if="running" class="mb-4">
      <div class="flex items-center gap-2 mb-3">
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
        <span class="text-sm text-gray-600 dark:text-gray-400">
          Running audit... {{ completedChecks }}/{{ totalExpected || '?' }} checks
        </span>
      </div>
      <div v-if="totalExpected" class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-3">
        <div class="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
          :style="{ width: `${(completedChecks / totalExpected) * 100}%` }"></div>
      </div>
    </div>

    <!-- Score -->
    <div v-if="latestAudit && latestAudit.status === 'completed'" class="flex items-center gap-4 mb-4 p-3 rounded-lg"
      :class="scoreBackground">
      <div class="text-2xl font-bold" :class="scoreColor">{{ latestAudit.score }}%</div>
      <div class="flex gap-3 text-xs">
        <span class="text-green-600 dark:text-green-400">{{ latestAudit.pass_count }} pass</span>
        <span class="text-amber-600 dark:text-amber-400">{{ latestAudit.warning_count }} warn</span>
        <span class="text-red-600 dark:text-red-400">{{ latestAudit.fail_count }} fail</span>
      </div>
    </div>

    <!-- Results grouped by category -->
    <div v-if="groupedResults && Object.keys(groupedResults).length > 0" class="space-y-3">
      <div v-for="(results, category) in groupedResults" :key="category"
        class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button @click="toggleCategory(category)"
          class="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 text-left">
          <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            {{ formatCategory(category) }}
          </span>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            {{ categorySummary(results) }}
          </span>
        </button>
        <div v-show="expandedCategories[category]" class="divide-y divide-gray-100 dark:divide-gray-700">
          <div v-for="result in results" :key="result.id" class="px-3 py-2">
            <button @click="toggleResult(result.id)" class="w-full flex items-start gap-2 text-left">
              <span class="mt-0.5 flex-shrink-0">
                <span v-if="result.status === 'pass'" class="text-green-500">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                </span>
                <span v-else-if="result.status === 'warning'" class="text-amber-500">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </span>
                <span v-else-if="result.status === 'fail'" class="text-red-500">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </span>
                <span v-else class="text-gray-400">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" /></svg>
                </span>
              </span>
              <div class="flex-1 min-w-0">
                <span class="text-sm text-gray-900 dark:text-gray-100">{{ result.title }}</span>
                <p v-if="result.details && !expandedResults[result.id]" class="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {{ result.details }}
                </p>
              </div>
            </button>
            <div v-if="expandedResults[result.id]" class="ml-6 mt-2 space-y-2">
              <p v-if="result.details" class="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ result.details }}</p>

              <!-- PSI individual runs -->
              <div v-if="result.metadata?.runs && result.metadata.runs.length > 1"
                class="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Individual Runs:</p>
                <div class="space-y-1.5">
                  <div v-for="(run, idx) in result.metadata.runs" :key="idx"
                    class="flex items-center gap-3 text-xs">
                    <span class="text-gray-500 dark:text-gray-400 w-12 shrink-0">Run {{ idx + 1 }}:</span>
                    <span class="font-semibold w-8" :class="psiScoreColor(run.performance)">{{ run.performance }}%</span>
                    <span v-if="run.core_web_vitals?.lcp" class="text-gray-500 dark:text-gray-400">
                      LCP: {{ run.core_web_vitals.lcp.display_value }}
                    </span>
                    <span v-if="run.core_web_vitals?.cls" class="text-gray-500 dark:text-gray-400">
                      CLS: {{ run.core_web_vitals.cls.display_value }}
                    </span>
                    <span v-if="run.core_web_vitals?.tbt" class="text-gray-500 dark:text-gray-400">
                      TBT: {{ run.core_web_vitals.tbt.display_value }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="result.suggestion" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2">
                <p class="text-xs font-medium text-blue-800 dark:text-blue-300 mb-1">Suggestion:</p>
                <p class="text-xs text-blue-700 dark:text-blue-400 whitespace-pre-wrap">{{ result.suggestion }}</p>
              </div>
              <div v-if="result.expected || result.actual" class="flex gap-4 text-xs">
                <span v-if="result.expected" class="text-gray-500 dark:text-gray-400">Expected: {{ result.expected }}</span>
                <span v-if="result.actual" class="text-gray-500 dark:text-gray-400">Actual: {{ result.actual }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Link Results -->
    <div v-if="linkResults && linkResults.length > 0" class="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button @click="showLinks = !showLinks"
        class="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 text-left">
        <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          Links Discovered
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ internalCount }} internal, {{ externalCount }} external
        </span>
      </button>
      <div v-show="showLinks" class="max-h-64 overflow-y-auto">
        <table class="w-full text-xs">
          <thead>
            <tr class="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th class="px-3 py-1.5 font-medium">URL</th>
              <th class="px-3 py-1.5 font-medium">Type</th>
              <th class="px-3 py-1.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 dark:divide-gray-700/50">
            <tr v-for="link in linkResults" :key="link.id">
              <td class="px-3 py-1.5 truncate max-w-[250px]">
                <span v-if="link.resolved_page_title" class="text-primary-600 dark:text-primary-400">{{ link.resolved_page_title }}</span>
                <span v-else class="text-gray-600 dark:text-gray-400">{{ link.url }}</span>
              </td>
              <td class="px-3 py-1.5">
                <span :class="link.link_type === 'internal' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'">
                  {{ link.link_type }}
                </span>
              </td>
              <td class="px-3 py-1.5">
                <span :class="statusLabelClass(link.status_label)">{{ link.status_label }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!latestAudit && !running && !loading" class="text-center py-6">
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-2">No audit results yet</p>
      <button @click="handleRunAudit" class="btn btn-primary btn-sm">Run First Audit</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { runAudit, getLatestAudit } from '~/javascript/api/page_audits'
import consumer from '~/channels/consumer'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['updated'])

const loading = ref(true)
const running = ref(false)
const latestAudit = ref(null)
const streamedResults = ref([])
const completedChecks = ref(0)
const totalExpected = ref(null)
const expandedCategories = ref({})
const expandedResults = ref({})
const showLinks = ref(false)
let subscription = null
let pollTimer = null

const displayResults = computed(() => {
  if (running.value && streamedResults.value.length > 0) return streamedResults.value
  return latestAudit.value?.results || []
})

const linkResults = computed(() => latestAudit.value?.link_results || [])

const groupedResults = computed(() => {
  const groups = {}
  displayResults.value.forEach((r) => {
    const cat = r.check_category || 'other'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(r)
  })
  return groups
})

const internalCount = computed(() => linkResults.value.filter((l) => l.link_type === 'internal').length)
const externalCount = computed(() => linkResults.value.filter((l) => l.link_type === 'external').length)

const scoreBackground = computed(() => {
  const score = latestAudit.value?.score
  if (score >= 80) return 'bg-green-50 dark:bg-green-900/20'
  if (score >= 50) return 'bg-amber-50 dark:bg-amber-900/20'
  return 'bg-red-50 dark:bg-red-900/20'
})

const scoreColor = computed(() => {
  const score = latestAudit.value?.score
  if (score >= 80) return 'text-green-600 dark:text-green-400'
  if (score >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
})

function formatCategory(cat) {
  const map = {
    meta: 'Meta / On-Page',
    headings: 'Headings',
    images: 'Images',
    schema: 'Schema / Structured Data',
    content: 'Content',
    links: 'Links',
    local_seo: 'Local SEO',
    performance: 'Performance',
    technical: 'Technical SEO'
  }
  return map[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function categorySummary(results) {
  const pass = results.filter((r) => r.status === 'pass').length
  const warn = results.filter((r) => r.status === 'warning').length
  const fail = results.filter((r) => r.status === 'fail').length
  const parts = []
  if (pass) parts.push(`${pass} pass`)
  if (warn) parts.push(`${warn} warn`)
  if (fail) parts.push(`${fail} fail`)
  return parts.join(', ')
}

function toggleCategory(cat) {
  expandedCategories.value[cat] = !expandedCategories.value[cat]
}

function toggleResult(id) {
  expandedResults.value[id] = !expandedResults.value[id]
}

function statusLabelClass(label) {
  const map = {
    OK: 'text-green-600 dark:text-green-400',
    Redirect: 'text-amber-600 dark:text-amber-400',
    'Not Found': 'text-red-600 dark:text-red-400',
    Error: 'text-red-600 dark:text-red-400',
    Unchecked: 'text-gray-400'
  }
  return map[label] || 'text-gray-500'
}

function psiScoreColor(score) {
  if (score >= 90) return 'text-green-600 dark:text-green-400'
  if (score >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

async function fetchLatest() {
  try {
    loading.value = true
    const { data } = await getLatestAudit(props.pageId)
    latestAudit.value = data
    if (data) {
      // Auto-expand categories with issues
      const results = data.results || []
      results.forEach((r) => {
        if (r.status !== 'pass') expandedCategories.value[r.check_category] = true
      })
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch audit:', e)
  } finally {
    loading.value = false
  }
}

async function handleRunAudit() {
  running.value = true
  streamedResults.value = []
  completedChecks.value = 0
  startPolling()

  try {
    const { data } = await runAudit(props.pageId)
    latestAudit.value = data
    emit('updated')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Audit failed:', e)
  } finally {
    running.value = false
    stopPolling()
    streamedResults.value = []
    fetchLatest()
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getLatestAudit(props.pageId)
      if (data && data.status === 'completed') {
        latestAudit.value = data
        running.value = false
        streamedResults.value = []
        const results = data.results || []
        results.forEach((r) => {
          if (r.status !== 'pass') expandedCategories.value[r.check_category] = true
        })
        emit('updated')
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function subscribeToChannel() {
  subscription = consumer.subscriptions.create(
    { channel: 'PageAuditChannel', website_page_id: props.pageId },
    {
      received(data) {
        if (data.event === 'check_completed' && data.result) {
          streamedResults.value.push(data.result)
          completedChecks.value++
          // Auto-expand categories with issues
          if (data.result.status !== 'pass') {
            expandedCategories.value[data.result.check_category] = true
          }
        }
        if (data.event === 'audit_completed') {
          totalExpected.value = completedChecks.value
          stopPolling()
        }
      }
    }
  )
}

onMounted(() => {
  fetchLatest()
  subscribeToChannel()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
